/* eslint-disable no-plusplus */
import htmlToText from 'html-to-text';
import { nl2br } from './get-merge-tag-mapping-with-values';

// Define classes to preserve during styling removal - shared across functions
const PRESERVE_CLASSES = [
  'merge-tag-chip-container',
  'variable-tag-chip-container',
  'spin-tag-chip-container',
  'date-tag-chip-container',
  'error-chip-container',
  'chip-cancel-icon',
  'merge-tag-chip',
  'variable-tag-chip',
  'date-tag-chip',
  'error-chip-icon',
  'error-chip',
  'spin-start',
  'spin-chips',
  'add-spin-icon',
  'spin-divider',
  'spin-tag-chip',
  'spin-tooltip',
  'tooltip-text',
  'content-tooltip',
  'editable-fallback',
  'default-fallback',
];

// Shared regex for better performance - compiled once
const PRESERVE_CLASSES_REGEX = new RegExp(PRESERVE_CLASSES.join('|'), 'i');

// Shared helper function
const containsPreserveClass = (content) => PRESERVE_CLASSES_REGEX.test(content);

export const parseContentForTextOnlySequence = (content) => {
  let htmlString = content;

  // Remove <style> and <link rel="stylesheet">
  htmlString = htmlString.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  htmlString = htmlString.replace(
    /<link[^>]*rel=["']stylesheet["'][^>]*>/gi,
    '',
  );

  // Convert header tags to normal <p> tags
  htmlString = htmlString
    .replace(/<h(\d)([^>]*)>/g, '<p>')
    .replace(/<\/h(\d)>/g, '</p>');

  // Remove strikethrough styling
  htmlString = htmlString
    .replace(/<s\b[^>]*>/gi, '<p>')
    .replace(/<\/s>/gi, '</p>');

  // Remove inline style attributes, but preserve chip container elements
  htmlString = htmlString.replace(/style="[^"]*"/g, (match, offset, string) => {
    // Check if this style attribute is within a chip container
    const beforeMatch = string.substring(0, offset);
    const lastOpenTagIndex = beforeMatch.lastIndexOf('<');

    if (lastOpenTagIndex !== -1) {
      const tagContent = beforeMatch.substring(lastOpenTagIndex);

      if (containsPreserveClass(tagContent)) {
        // Preserve the style attribute for chip containers
        return match;
      }
    }

    // Remove style attribute for other elements
    return '';
  });

  // Remove span and iframe tags, but preserve chip containers
  htmlString = htmlString.replace(
    /<(span|iframe)([^>]*)>/g,
    (match, tagName, attributes) => {
      if (containsPreserveClass(attributes)) {
        // Preserve the tag for chip containers
        return match;
      }

      // Remove other span/iframe tags
      return '';
    },
  );

  // Format text - but preserve chip containers
  htmlString = htmlString
    // Replace opening tags like <iframe>, <input>, etc. except <i> and <img>
    // But skip if it's part of a chip container
    ?.replace(
      /<(?!i\b|img\b)(i[a-z0-9-]*)([^>]*)>/gi,
      (match, tagName, attributes) => {
        if (containsPreserveClass(attributes)) {
          // Preserve the tag for chip containers
          return match;
        }
        return '<p>';
      },
    )
    // Replace matching closing tags </iframe>, etc., except </i>
    // But skip if it's part of a chip container
    ?.replace(
      /<\/(?!i\b|img\b)(i[a-z0-9-]*)>/gi,
      (match, tagName) =>
        // We need to check if this closing tag belongs to a chip container
        // This is more complex, so we'll use a simpler approach
        '</p>',
    );

  return htmlString;
};

const transformHtmlForText = (html: string): string => {
  let transformed = html;

  // 1) Links: keep only inner text, ignore href, preserve trailing spaces
  // But preserve chip container links
  transformed = transformed.replace(
    /<a\b[^>]*>([\s\S]*?)<\/a>(\s*)/gi,
    (match, inner, trailingSpace) => {
      if (containsPreserveClass(inner)) {
        // Preserve the entire link for chip containers
        return match;
      }

      const textContent = inner
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
      return textContent + trailingSpace;
    },
  );

  // 2) Images: skip entirely, but preserve chip container images
  transformed = transformed.replace(/<img\b[^>]*>/gi, (match) => {
    // Check if this is a chip cancel icon image
    if (match.includes('chip-cancel-icon') || match.includes('Chip Cancel')) {
      // Preserve chip cancel icon images
      return match;
    }
    // Remove other images
    return '';
  });

  // 3) Lists: treat ul/ol as block containers and li as inline items
  transformed = transformed
    .replace(/<\/?ul\b[^>]*>/gi, (m) => (m.startsWith('</') ? '</p>' : '<p>'))
    .replace(/<\/?ol\b[^>]*>/gi, (m) => (m.startsWith('</') ? '</p>' : '<p>'))
    .replace(/<li\b[^>]*>/gi, '')
    .replace(/<\/li>/gi, ' ');

  // 4) Tables: render as simple data rows (cells separated by tabs, rows by newlines)
  transformed = transformed
    .replace(/<\/?table\b[^>]*>/gi, '')
    .replace(/<thead\b[^>]*>/gi, '')
    .replace(/<\/thead>/gi, '')
    .replace(/<tbody\b[^>]*>/gi, '')
    .replace(/<\/tbody>/gi, '')
    .replace(/<tfoot\b[^>]*>/gi, '')
    .replace(/<\/tfoot>/gi, '')
    .replace(/<tr\b[^>]*>/gi, '')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<t[dh]\b[^>]*>/gi, '')
    .replace(/<\/t[dh]>/gi, '\t');

  // 5) Blockquotes: treat as inline content
  transformed = transformed
    .replace(/<blockquote\b[^>]*>/gi, '')
    .replace(/<\/blockquote>/gi, '');

  return transformed;
};

export const convertHtmlToText = (
  content: string,
  parseContentWithNl2br = true,
): string => {
  // First, extract and preserve chip containers and their nested elements
  const chipContainers: string[] = [];
  const chipPlaceholders: string[] = [];

  let modifiedContent = content;

  // Extract chip containers with all their nested content (including other chips inside spin tags)
  // Use a more robust approach to handle nested spans properly
  const extractChipContainers = (html: string) => {
    const containers: string[] = [];
    const placeholders: string[] = [];
    let result = html;

    // Find all chip container patterns - including error chips
    const chipPatterns = [
      'merge-tag-chip-container',
      'variable-tag-chip-container',
      'spin-tag-chip-container',
      'date-tag-chip-container',
      'error-chip-container',
    ];

    const patternRegex = new RegExp(
      `<span[^>]*class="[^"]*(?:${chipPatterns?.join('|')})[^"]*"[^>]*>`,
      'gi',
    );

    // Process all matches iteratively by finding them in the current result
    // This avoids index shifting issues
    let match = patternRegex?.exec(result);
    while (match !== null) {
      const startIndex = match.index;
      const tagStart = match[0];

      // Find the matching closing tag by counting nested spans
      let depth = 1;
      let currentIndex = startIndex + tagStart.length;
      let endIndex = -1;

      while (depth > 0 && currentIndex < result?.length) {
        const nextOpen = result?.indexOf('<span', currentIndex);
        const nextClose = result?.indexOf('</span>', currentIndex);

        if (nextClose === -1) break;

        if (nextOpen !== -1 && nextOpen < nextClose) {
          // Found an opening tag before closing tag
          depth++;
          currentIndex = nextOpen + 5; // Move past '<span'
        } else {
          // Found a closing tag
          depth--;
          if (depth === 0) {
            endIndex = nextClose + 7;
            break;
          }
          currentIndex = nextClose + 7;
        }
      }

      if (endIndex !== -1) {
        const container = result.substring(startIndex, endIndex);
        const placeholder = `__CHIP_CONTAINER_${containers?.length}__`;
        containers?.push(container);
        placeholders?.push(placeholder);
        result =
          result.substring(0, startIndex) +
          placeholder +
          result.substring(endIndex);

        // Reset regex to search from the start of the modified result
        patternRegex.lastIndex = 0;
        match = patternRegex.exec(result);
      } else {
        // If we couldn't find closing tag, skip this match and continue
        match = patternRegex.exec(result);
      }
    }

    return { result, containers, placeholders };
  };

  const {
    result: extractedContent,
    containers,
    placeholders,
  } = extractChipContainers(modifiedContent);
  modifiedContent = extractedContent;
  chipContainers?.push(...containers);
  chipPlaceholders?.push(...placeholders);

  // Process the content with transformHtmlForText (this will handle non-chip content)
  modifiedContent = transformHtmlForText(modifiedContent);

  // Convert to text using htmlToText
  modifiedContent = htmlToText.htmlToText(modifiedContent, {
    wordwrap: false,
    preserveWhitespace: true,
  });

  // Restore chip containers after htmlToText conversion
  // Process in reverse order to handle nested replacements correctly
  for (let i = chipPlaceholders?.length - 1; i >= 0; i--) {
    const placeholder = chipPlaceholders[i];
    const container = chipContainers[i];
    // Use split and join for reliable replacement of all occurrences
    modifiedContent = modifiedContent?.split(placeholder).join(container);
  }

  if (!parseContentWithNl2br) {
    return modifiedContent;
  }

  return nl2br(modifiedContent, undefined, true);
};
